// Vie quotidienne et logement : ce qui casse, ce qui coûte, ce qu'on peut faire soi-même.
import type { GameState } from '../../engine/state';
import type { EventDef, EventParams } from '../events';
import { findSubscription, hasSubscription } from '../../game/subscriptions';
import { between, income, simpleEvent, working, type Effects } from './kit';

/** Un logement à soi : un studio ou une colocation, pas la chambre chez les parents. */
function ownPlace(state: GameState): boolean {
  return state.housing !== 'parents';
}

/** En location : un loyer, un bail, un propriétaire. */
function renting(state: GameState): boolean {
  return state.housing === 'coloc' || state.housing === 'studio';
}

/** Un montant au hasard, plus modeste pendant les études. */
function byStage(state: GameState, student: [number, number], adult: [number, number]): number {
  const [min, max] = state.stage === 'prologue' ? student : adult;
  return between(state, min, max);
}

/** Adopter le chat : les frais d'adoption, puis les croquettes et la litière chaque mois. */
function adoptCat(p: EventParams): Effects {
  return {
    cash: -p.adoption,
    wellbeing: 4,
    addExpense: { id: 'courses', amount: p.monthly },
    flag: 'chat_adopte',
  };
}

export const EVENTS: EventDef[] = [
  simpleEvent({
    id: 'machine_a_laver',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 6,
    condition: (state) => state.housing !== 'parents',
    params: (state) => ({ repair: between(state, 90, 140), cost: between(state, 300, 420) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'reparer', effects: (_, p) => ({ cash: -p.repair, energy: -2 }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'credit', effects: (_, p) => ({ loan: { amount: p.cost, rate: 0.12, months: 10 } }) },
    ],
  }),
  simpleEvent({
    id: 'vide_grenier',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [4, 5, 6, 7, 8, 9],
    cooldownDays: 300,
    params: (state) => ({ gain: between(state, 60, 150) }),
    choices: [
      { id: 'vendre', effects: (_, p) => ({ cash: p.gain, energy: -4 }) },
      { id: 'donner', effects: { wellbeing: 2 } },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'offre_box',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => working(state) && state.housing !== 'parents' && hasSubscription(state, 'box_internet'),
    // L'offre de bienvenue d'un autre opérateur : environ 40 % de moins.
    params: (state) => ({ saving: Math.round((findSubscription(state, 'box_internet')?.amount ?? 0) * 0.4) }),
    choices: [
      {
        id: 'changer',
        effects: (_, p) => ({ changeSubscription: { id: 'box_internet', amount: -p.saving }, energy: -2 }),
      },
      { id: 'garder' },
    ],
  }),

  // Les coups durs de la maison : la dépense est inévitable, seule la manière de payer change.
  simpleEvent({
    id: 'frigo_en_panne',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 6,
    maxCount: 2,
    condition: ownPlace,
    params: (state) => ({
      cost: byStage(state, [250, 350], [380, 550]),
      low: byStage(state, [130, 180], [180, 260]),
      van: between(state, 40, 70),
    }),
    page: 'usure',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'reconditionne', effects: (_, p) => ({ cash: -p.low }) },
      { id: 'parents', effects: (_, p) => ({ cash: -p.van, energy: -5 }) },
    ],
  }),
  simpleEvent({
    id: 'degat_des_eaux',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 7,
    maxCount: 2,
    condition: (state) => ownPlace(state) && state.expenses.assurance !== undefined,
    params: (state) => ({ franchise: between(state, 150, 250), paint: between(state, 35, 60) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.franchise }) },
      { id: 'carte', effects: (_, p) => ({ cash: -p.franchise }) },
      { id: 'repeindre', effects: (_, p) => ({ cash: -p.paint, energy: -5 }) },
    ],
  }),
  simpleEvent({
    id: 'cles_perdues',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 6,
    maxCount: 2,
    condition: renting,
    params: (state) => ({ cost: between(state, 180, 320), copy: between(state, 15, 35) }),
    choices: [
      { id: 'serrurier', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      {
        id: 'ami',
        visible: (state) => state.housing !== 'coloc',
        effects: (_, p) => ({ cash: -p.copy, energy: -3, wellbeing: -1 }),
      },
      {
        // En colocation, Emma a ses clés : il suffit d'attendre son retour.
        id: 'emma',
        visible: (state) => state.housing === 'coloc',
        effects: (_, p) => ({ cash: -p.copy, energy: -3, wellbeing: -1 }),
      },
    ],
  }),
  simpleEvent({
    id: 'punaises_de_lit',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 8,
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({
      cost: byStage(state, [300, 450], [400, 650]),
      diy: byStage(state, [60, 100], [80, 130]),
    }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'credit', effects: (_, p) => ({ loan: { amount: p.cost, rate: 0.1, months: 4 } }) },
      { id: 'soi', effects: (_, p) => ({ cash: -p.diy, energy: -6, wellbeing: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'regularisation_charges',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 3,
    condition: ownPlace,
    params: (state) => {
      const cost = byStage(state, [60, 140], [90, 220]);
      // Une erreur de décompte trouvée en vérifiant : il reste un peu plus de la moitié à payer.
      return { cost, reduced: Math.round(cost * 0.55) };
    },
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'carte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'verifier', effects: (_, p) => ({ cash: -p.reduced, energy: -3, knowledge: 1 }) },
    ],
  }),
  simpleEvent({
    // La facture annuelle compare les mensualités à la consommation réelle.
    id: 'regularisation_electricite',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 4,
    maxCount: 3,
    condition: ownPlace,
    params: (state) => ({ due: byStage(state, [50, 110], [80, 180]) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.due }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.due }) },
      { id: 'etaler', effects: (_, p) => ({ loan: { amount: p.due, rate: 0, months: 3 } }) },
    ],
  }),
  simpleEvent({
    id: 'hiver_froid',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [12, 1, 2],
    cooldownDays: 300,
    condition: ownPlace,
    params: (state) => ({
      extra: Math.max(10, Math.round((state.expenses.energie?.amount ?? 40) * 0.5)),
      plaid: between(state, 20, 30),
    }),
    choices: [
      { id: 'chauffer', effects: (_, p) => ({ cash: -p.extra }) },
      { id: 'plaid', effects: (_, p) => ({ cash: -p.plaid, wellbeing: -1 }) },
      { id: 'pull', effects: { energy: -2, wellbeing: -3 } },
    ],
  }),
  simpleEvent({
    id: 'cheque_energie',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    // Envoyé aux foyers aux revenus modestes : pendant les études, ou avec un très petit salaire.
    condition: (state) => ownPlace(state) && income(state) <= 1000 * state.priceLevel,
    // Un montant fixé par l'État : il ne suit pas la hausse des prix.
    params: () => ({ amount: 150 }),
    choices: [
      { id: 'utiliser', effects: (_, p) => ({ cash: p.amount, energy: -1 }) },
      { id: 'oublier' },
    ],
  }),
  simpleEvent({
    id: 'hausse_loyer',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 4,
    maxCount: 2,
    condition: renting,
    params: (state) => {
      const hausse = byStage(state, [8, 14], [12, 25]);
      return { hausse, petite: Math.round(hausse / 2) };
    },
    page: 'inflation',
    choices: [
      { id: 'accepter', effects: (_, p) => ({ addExpense: { id: 'logement', amount: p.hausse } }) },
      {
        id: 'verifier',
        effects: (_, p) => ({ addExpense: { id: 'logement', amount: p.petite }, energy: -2, knowledge: 1 }),
      },
    ],
  }),
  simpleEvent({
    id: 'evier_bouche',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 2,
    condition: ownPlace,
    params: (state) => ({
      urgent: between(state, 130, 190),
      lundi: between(state, 70, 100),
      outil: between(state, 15, 25),
    }),
    choices: [
      { id: 'urgence', effects: (_, p) => ({ cash: -p.urgent }) },
      { id: 'lundi', effects: (_, p) => ({ cash: -p.lundi, energy: -1, wellbeing: -1 }) },
      {
        id: 'furet',
        effects: (_, p) => ({ cash: -p.outil, energy: -4, xp: { skill: 'technique', amount: 8 } }),
      },
    ],
  }),
  simpleEvent({
    // La suite de l'adoption : seulement pour qui a un chat.
    id: 'veterinaire_chat',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 7,
    maxCount: 2,
    condition: (state) => state.flags.chat_adopte === true,
    params: (state) => ({ cost: between(state, 220, 420), low: between(state, 60, 110) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'carte', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'dispensaire', effects: (_, p) => ({ cash: -p.low, energy: -4, wellbeing: -1 }) },
    ],
  }),

  // Les bonnes surprises.
  simpleEvent({
    id: 'releve_compteur',
    kind: 'chance',
    pleasureCategory: 'repas',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 3,
    condition: ownPlace,
    params: (state) => ({ gain: byStage(state, [40, 100], [60, 180]) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'resto', effects: { pleasure: 3 } },
    ],
  }),
  simpleEvent({
    id: 'seance_photo',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ gain: between(state, 150, 300) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, energy: -2, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain, energy: -2 }) },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'encombrants',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ gain: between(state, 50, 110) }),
    choices: [
      { id: 'garder', effects: { energy: -3, wellbeing: 2 } },
      { id: 'revendre', effects: (_, p) => ({ cash: p.gain, energy: -5 }) },
      { id: 'laisser' },
    ],
  }),
  simpleEvent({
    id: 'sous_location_ete',
    kind: 'chance',
    stages: ['prologue'],
    season: [5, 6],
    cooldownDays: 300,
    maxCount: 3,
    condition: (state) => state.path === 'studies' && ownPlace(state),
    params: (state) => ({ gain: between(state, 300, 400) }),
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, energy: -3, wellbeing: 1 }) },
      { id: 'profiter', effects: (_, p) => ({ cash: p.gain, energy: -3 }) },
      { id: 'garder', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'peinture_contre_loyer',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: renting,
    params: (state) => {
      const gain = between(state, 120, 220);
      return { gain, half: Math.round(gain / 2) };
    },
    choices: [
      {
        id: 'peindre',
        effects: (_, p) => ({ cash: p.gain, energy: -6, xp: { skill: 'technique', amount: 10 } }),
      },
      { id: 'ami', effects: (_, p) => ({ cash: p.half, energy: -3, wellbeing: 2 }) },
      { id: 'peintre' },
    ],
  }),

  // Les envies pour la maison.
  simpleEvent({
    id: 'canape_moelleux',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ price: between(state, 700, 1100), used: between(state, 180, 320) }),
    page: 'loin_des_yeux',
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.price, pleasure: 5 }) },
      { id: 'occasion', effects: (_, p) => ({ cash: -p.used, energy: -3, pleasure: 3 }) },
      { id: 'garder', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'jardinerie',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    condition: ownPlace,
    params: (state) => ({ price: between(state, 45, 80), potager: between(state, 20, 35) }),
    choices: [
      { id: 'monstera', effects: (_, p) => ({ cash: -p.price, pleasure: 3 }) },
      { id: 'potager', effects: (_, p) => ({ cash: -p.potager, energy: -1, wellbeing: 2 }) },
      { id: 'bouture', effects: { energy: -1, wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'aspirateur_robot',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ price: between(state, 200, 350), basic: between(state, 60, 100) }),
    choices: [
      { id: 'robot', effects: (_, p) => ({ cash: -p.price, pleasure: 4 }) },
      { id: 'classique', effects: (_, p) => ({ cash: -p.basic, pleasure: 1 }) },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'sapin_noel',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [12],
    cooldownDays: 300,
    maxCount: 3,
    condition: ownPlace,
    params: (state) => ({ price: byStage(state, [30, 50], [45, 80]) }),
    choices: [
      { id: 'sapin', effects: (_, p) => ({ cash: -p.price, pleasure: 3 }) },
      { id: 'plante', effects: { energy: -1, wellbeing: 1 } },
      { id: 'rien' },
    ],
  }),
  simpleEvent({
    id: 'deco_cocooning',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ price: byStage(state, [45, 70], [70, 130]), small: between(state, 20, 35) }),
    choices: [
      { id: 'tout', effects: (_, p) => ({ cash: -p.price, pleasure: 4 }) },
      { id: 'plaid', effects: (_, p) => ({ cash: -p.small, pleasure: 2 }) },
      { id: 'rien' },
    ],
  }),

  // Les grands moments de la maison.
  simpleEvent({
    // Un logement plus grand dans le même immeuble ; l'état des lieux du studio se règle au départ.
    id: 'deux_pieces',
    kind: 'etape',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => state.housing === 'studio' && working(state),
    params: (state) => ({
      hausse: between(state, 60, 100),
      frais: between(state, 50, 90),
      retenue: between(state, 150, 300),
    }),
    page: 'train_de_vie',
    choices: [
      {
        id: 'remettre',
        effects: (_, p) => ({
          cash: -p.frais,
          energy: -6,
          wellbeing: 5,
          addExpense: { id: 'logement', amount: p.hausse },
        }),
      },
      {
        id: 'retenue',
        effects: (_, p) => ({
          cash: -p.retenue,
          energy: -2,
          wellbeing: 5,
          addExpense: { id: 'logement', amount: p.hausse },
        }),
      },
      { id: 'rester' },
    ],
  }),
  simpleEvent({
    id: 'adopter_chat',
    kind: 'etape',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ adoption: between(state, 150, 220), monthly: between(state, 25, 40) }),
    choices: [
      { id: 'adopter', effects: (_, p) => adoptCat(p) },
      { id: 'accueil', effects: { energy: -1, wellbeing: 3 } },
      { id: 'reflechir', effects: { followUp: { event: 'adopter_chat_suite', days: 7 } } },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    // Une semaine après « Y réfléchir encore une semaine ».
    id: 'adopter_chat_suite',
    kind: 'etape',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [{ id: 'adopter', effects: (_, p) => adoptCat(p) }, { id: 'renoncer' }],
  }),

  // Le quotidien à la maison : les voisins, le propriétaire, la coloc, les petites économies.
  simpleEvent({
    id: 'chauffe_eau_froid',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ piscine: between(state, 20, 35) }),
    choices: [
      { id: 'froid', effects: { wellbeing: -2 } },
      { id: 'piscine', effects: (_, p) => ({ cash: -p.piscine, energy: -2, wellbeing: 1 }) },
      {
        id: 'relancer',
        effects: { energy: -2, wellbeing: -1, xp: { skill: 'communication', amount: 10 } },
      },
    ],
  }),
  simpleEvent({
    id: 'voisin_bruyant',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    condition: ownPlace,
    choices: [
      { id: 'frapper', effects: { energy: -1, wellbeing: 1 } },
      { id: 'fete', effects: { energy: -4, wellbeing: 3 } },
      { id: 'oreiller', effects: { energy: -2, wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'voisine_plantes',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    condition: ownPlace,
    params: (state) => ({ gain: between(state, 30, 60) }),
    choices: [
      { id: 'accepter', effects: (_, p) => ({ cash: p.gain, energy: -2 }) },
      { id: 'gratuit', effects: { energy: -2, wellbeing: 3 } },
      { id: 'decliner' },
    ],
  }),
  simpleEvent({
    id: 'lucas_loyer',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => state.housing === 'coloc',
    params: (state) => ({ missing: between(state, 150, 250) }),
    choices: [
      {
        id: 'avancer',
        effects: (_, p) => ({
          cash: -p.missing,
          wellbeing: 1,
          followUp: { event: 'lucas_rembourse', days: 42 },
        }),
      },
      { id: 'delai', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    // Six semaines après l'avance du loyer.
    id: 'lucas_rembourse',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.missing, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.missing }) },
    ],
  }),
  simpleEvent({
    id: 'caisse_commune',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => state.housing === 'coloc',
    params: (state) => ({ saving: between(state, 10, 20) }),
    choices: [
      { id: 'rejoindre', effects: (_, p) => ({ addExpense: { id: 'courses', amount: -p.saving }, energy: -1 }) },
      {
        id: 'comptes',
        effects: (_, p) => ({
          addExpense: { id: 'courses', amount: -p.saving },
          energy: -2,
          xp: { skill: 'organisation', amount: 15 },
        }),
      },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'vague_de_froid',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [12, 1, 2],
    cooldownDays: 300,
    maxCount: 3,
    condition: ownPlace,
    params: (state) => ({ extra: byStage(state, [30, 60], [45, 90]), kit: between(state, 15, 30) }),
    choices: [
      { id: 'chauffer', effects: (_, p) => ({ cash: -p.extra, wellbeing: 1 }) },
      { id: 'calfeutrer', effects: (_, p) => ({ cash: -p.kit, energy: -2 }) },
      { id: 'pulls', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'canicule',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [7, 8],
    cooldownDays: 300,
    maxCount: 2,
    params: (state) => ({ fan: between(state, 20, 40), clim: between(state, 250, 400) }),
    choices: [
      { id: 'clim', effects: (_, p) => ({ cash: -p.clim, pleasure: 4 }) },
      { id: 'ventilateur', effects: (_, p) => ({ cash: -p.fan, pleasure: 2 }) },
      { id: 'volets', effects: { energy: -12, wellbeing: -10 } },
    ],
  }),
  simpleEvent({
    id: 'ampoules_led',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({
      cost: between(state, 30, 50),
      saving: between(state, 5, 8),
      connected: between(state, 90, 140),
    }),
    choices: [
      { id: 'led', effects: (_, p) => ({ cash: -p.cost, addExpense: { id: 'energie', amount: -p.saving } }) },
      {
        id: 'connectees',
        effects: (_, p) => ({
          cash: -p.connected,
          pleasure: 2,
          addExpense: { id: 'energie', amount: -p.saving },
        }),
      },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'meuble_bibliotheque',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({
      neuf: byStage(state, [60, 100], [120, 200]),
      occasion: byStage(state, [20, 40], [40, 80]),
    }),
    choices: [
      { id: 'neuve', effects: (_, p) => ({ cash: -p.neuf, energy: -3, pleasure: 2 }) },
      { id: 'occasion', effects: (_, p) => ({ cash: -p.occasion, energy: -4, pleasure: 1 }) },
      { id: 'cartons', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'cuisine_du_dimanche',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ saving: between(state, 20, 40) }),
    page: 'budget',
    choices: [
      {
        id: 'habitude',
        effects: (_, p) => ({
          addExpense: { id: 'courses', amount: -p.saving },
          energy: -3,
          xp: { skill: 'organisation', amount: 10 },
        }),
      },
      { id: 'essayer', effects: { energy: -2, wellbeing: 1 } },
      { id: 'improviser' },
    ],
  }),
  simpleEvent({
    id: 'panier_anti_gaspi',
    kind: 'quotidien',
    pleasureCategory: 'repas',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ price: between(state, 4, 6), saving: between(state, 10, 20) }),
    choices: [
      { id: 'habitude', effects: (_, p) => ({ addExpense: { id: 'courses', amount: -p.saving }, energy: -1 }) },
      { id: 'essayer', effects: (_, p) => ({ cash: -p.price, pleasure: 1 }) },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    id: 'fete_des_voisins',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [5, 6],
    cooldownDays: 300,
    maxCount: 3,
    condition: ownPlace,
    params: (state) => ({ cost: between(state, 15, 25), ingredients: between(state, 6, 10) }),
    choices: [
      { id: 'tarte', effects: (_, p) => ({ cash: -p.cost, wellbeing: 3 }) },
      { id: 'cake', effects: (_, p) => ({ cash: -p.ingredients, energy: -2, wellbeing: 3 }) },
      { id: 'bonjour', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'travaux_facade',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ gain: byStage(state, [40, 80], [60, 120]) }),
    choices: [
      { id: 'remise', effects: (_, p) => ({ cash: p.gain, energy: -2, wellbeing: -1, knowledge: 1 }) },
      { id: 'bibliotheque', effects: { energy: -2 } },
      { id: 'patience', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'colis_introuvable',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    condition: ownPlace,
    params: (state) => ({ value: byStage(state, [20, 50], [30, 90]) }),
    choices: [
      { id: 'voisins', effects: { energy: -2, wellbeing: 1 } },
      { id: 'reclamer', effects: (_, p) => ({ cash: p.value, energy: -2, knowledge: 1 }) },
      { id: 'laisser', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'internet_coupe',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    condition: ownPlace,
    params: (state) => ({ data: between(state, 10, 20), geste: between(state, 15, 30) }),
    choices: [
      { id: 'recharge', effects: (_, p) => ({ cash: -p.data, pleasure: 2 }) },
      { id: 'geste', effects: (_, p) => ({ cash: p.geste, energy: -2, wellbeing: -1 }) },
      { id: 'lire', effects: { wellbeing: 1 } },
    ],
  }),
  simpleEvent({
    id: 'assurance_habitation',
    kind: 'quotidien',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => {
      const insurance = state.expenses.assurance;
      return insurance !== undefined && insurance.amount >= 8 && !insurance.optimized;
    },
    params: (state) => ({ saving: Math.max(2, Math.round((state.expenses.assurance?.amount ?? 10) * 0.3)) }),
    choices: [
      {
        id: 'changer',
        effects: (_, p) => ({ addExpense: { id: 'assurance', amount: -p.saving }, energy: -2, knowledge: 1 }),
      },
      {
        id: 'negocier',
        effects: (_, p) => ({
          addExpense: { id: 'assurance', amount: -Math.round(p.saving / 2) },
          energy: -1,
          xp: { skill: 'communication', amount: 5 },
        }),
      },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'moisissures',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ cost: between(state, 120, 180) }),
    choices: [
      {
        id: 'proprietaire',
        effects: { energy: -2, wellbeing: -1, xp: { skill: 'communication', amount: 5 } },
      },
      { id: 'deshumidificateur', effects: (_, p) => ({ cash: -p.cost, wellbeing: 1 }) },
      { id: 'aerer', effects: { energy: -1, wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'perceuse_voisine',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ drill: between(state, 40, 70) }),
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.drill, xp: { skill: 'technique', amount: 5 } }) },
      { id: 'emprunter', effects: { energy: -2, wellbeing: 2 } },
      { id: 'poser' },
    ],
  }),
];
